import { Navigate, type RouteObject } from 'react-router';
import { lazyComponent } from '@/platform/lazyComponent';
import { Require } from '@/platform/Require';

const AthletesPage = lazyComponent(() => import('./AthletesPage').then((module) => module.AthletesPage));
const DashboardPage = lazyComponent(() => import('./dashboard/DashboardPage').then((module) => module.DashboardPage));
const AthleteImportPage = lazyComponent(() => import('./import/AthleteImportPage').then((module) => module.AthleteImportPage));
const AthleteFormPage = lazyComponent(() => import('./profile/AthleteFormPage').then((module) => module.AthleteFormPage));
const AthleteLayout = lazyComponent(() => import('./profile/AthleteLayout').then((module) => module.AthleteLayout));
const AthleteOverview = lazyComponent(() => import('./profile/AthleteOverview').then((module) => module.AthleteOverview));
const AthleteProfileTab = lazyComponent(() => import('./profile/AthleteProfileTab').then((module) => module.AthleteProfileTab));
const AthleteTeamsTab = lazyComponent(() => import('./profile/AthleteTeamsTab').then((module) => module.AthleteTeamsTab));
const AthleteTimelineTab = lazyComponent(() => import('./profile/AthleteTimelineTab').then((module) => module.AthleteTimelineTab));
const SportTeamsTab = lazyComponent(() => import('./teams/SportTeamsTab').then((module) => module.SportTeamsTab));
const StaffPage = lazyComponent(() => import('./teams/StaffPage').then((module) => module.StaffPage));
const TeamDetailPage = lazyComponent(() => import('./teams/TeamDetailPage').then((module) => module.TeamDetailPage));
const TeamSeasonPage = lazyComponent(() => import('./teams/TeamSeasonPage').then((module) => module.TeamSeasonPage));
const TeamsPage = lazyComponent(() => import('./teams/TeamsPage').then((module) => module.TeamsPage));
const CoachDashboardPage = lazyComponent(() => import('./coach/CoachDashboardPage').then((module) => module.CoachDashboardPage));
const CoachSquadPage = lazyComponent(() => import('./coach/CoachSquadPage').then((module) => module.CoachSquadPage));
const TeamSelectionPage = lazyComponent(() => import('./coach/TeamSelectionPage').then((module) => module.TeamSelectionPage));
const TeamSheetPage = lazyComponent(() => import('./coach/TeamSheetPage').then((module) => module.TeamSheetPage));

/** The Teams tab under a sport (`/sports/:sportId/teams`): Athletes adds it to Base's sport page (the route id `sport`). */
export const athletesSportRoutes: RouteObject[] = [{ path: 'teams', element: <SportTeamsTab /> }];

/**
 * The athlete's page has the id `athlete`: other modules add their tabs under it (`routeExtensions` in their module.ts),
 * and `AthleteLayout` lists them next to these.
 */
export const athletesRoutes: RouteObject[] = [
  { path: 'coach', element: <Require permission="team_selection.view"><CoachDashboardPage /></Require> },
  { path: 'coach/squad', element: <Require permission="team_selection.view"><CoachSquadPage /></Require> },
  { path: 'coach/team-selection', element: <Require permission="team_selection.view"><TeamSelectionPage /></Require> },
  { path: 'coach/team-selection/:selectionId', element: <Require permission="team_selection.view"><TeamSelectionPage /></Require> },
  { path: 'coach/team-sheet/:selectionId', element: <Require permission="team_selection.view"><TeamSheetPage /></Require> },
  { path: 'dashboard', element: <Require permission="athlete.view"><DashboardPage /></Require> },

  { path: 'athletes', element: <Require permission="athlete.view"><AthletesPage /></Require> },
  { path: 'athletes/new', element: <Require permission="athlete.create"><AthleteFormPage /></Require> },
  { path: 'athletes/import', element: <Require permission="athlete.create"><AthleteImportPage /></Require> },
  { path: 'athletes/:id/edit', element: <Require permission="athlete.edit"><AthleteFormPage /></Require> },
  {
    id: 'athlete',
    path: 'athletes/:id',
    element: <Require permission="athlete.view"><AthleteLayout /></Require>,
    children: [
      { index: true, element: <AthleteOverview /> },
      { path: 'profile', element: <AthleteProfileTab /> },
      { path: 'teams', element: <AthleteTeamsTab /> },
      { path: 'timeline', element: <AthleteTimelineTab /> },
    ],
  },

  // The teams are permanent; each takes part in a season through a team-season, which has its own squad, staff and places.
  { path: 'teams', element: <Require permission="team.view"><TeamsPage /></Require> },
  { path: 'staff', element: <Require permission="team_season.view"><StaffPage /></Require> },
  // Teams has no availability view of its own any more: the full list is the Team availability report. Old links and bookmarks land there.
  { path: 'teams/availability', element: <Navigate to="/reports/team-availability" replace /> },
  { path: 'teams/:id', element: <Require permission="team.view"><TeamDetailPage /></Require> },
  { path: 'team-seasons/:id', element: <Require permission="team_season.view"><TeamSeasonPage /></Require> },
];
